import { IXmlNode } from '@/app-modules/server-communications';
import { injectable } from 'inversify';
import { CallsData, CatchUpData, NextGameData, ResetData, PrizeData, BoxNumbersData } from '@/components/game/_domain';
import { DefaultCatchUpData, DefaultCallsData, IPrizeClassDescriptor, DefaultNextGameData, DefaultResetData, DefaultBoxNumbers } from '@/components/game/implementations/_default/_domain/models';
import { GamePrizeDescriptors } from '@/components/game/_domain/server/_factory';
import { GameIdentity } from '@/components/game/_domain/models';

interface IPrizeDataFactory<T extends PrizeData> {
  getInstance(gameIdentity: GameIdentity, node: IXmlNode): T | null;
}

interface ICallsDataFactory extends IPrizeDataFactory<CallsData> {
}

interface ICatchUpDataFactory extends IPrizeDataFactory<CatchUpData> {
}

interface INextGameDataFactory extends IPrizeDataFactory<NextGameData> {
}

interface IResetDataFactory extends IPrizeDataFactory<ResetData> {
}

interface IBoxNumbersDataFactory extends IPrizeDataFactory<BoxNumbersData> {
}

@injectable()
abstract class PrizeDataFactory<T extends PrizeData> implements IPrizeDataFactory<T> {
  public getInstance(gameIdentity: GameIdentity, node: IXmlNode): T {
    const gamePrizeDescriptor = this.getGamePrizeDescriptor(gameIdentity.nameForConfiguration);
    const gamePrizeClass = this.getGamePrizeClass(gamePrizeDescriptor) || this.getGamePrizeDefaultClass();
    return new gamePrizeClass(node, gameIdentity);
  }

  protected abstract getGamePrizeClass(gamePrizeClassDescriptor: IPrizeClassDescriptor): new(node: IXmlNode, gameIdentity?: GameIdentity) => T;

  protected abstract getGamePrizeDefaultClass(): new(node: IXmlNode) => T;

  private getGamePrizeDescriptor(gameName: string): IPrizeClassDescriptor {
    const gamePrizeClasses = GamePrizeDescriptors.getGamePrizeClasses();
    const gamePrizeDescriptor = gamePrizeClasses[gameName];

    if (!gamePrizeDescriptor) {
      return {
        callsDataClass: DefaultCallsData,
        catchUpDataClass: DefaultCatchUpData,
        nextGameDataClass: DefaultNextGameData,
        resetDataClass: DefaultResetData,
        boxNumbersDataClass: DefaultBoxNumbers
      };
    }

    return gamePrizeDescriptor;
  }
}

@injectable()
class CallsDataFactory extends PrizeDataFactory<CallsData> implements ICallsDataFactory {
  protected getGamePrizeClass(gamePrizeClassDescriptor: IPrizeClassDescriptor): new (node: IXmlNode) => CallsData {
    return gamePrizeClassDescriptor.callsDataClass;
  }

  protected getGamePrizeDefaultClass(): new (node: IXmlNode) => CallsData {
    return DefaultCallsData;
  }
}

@injectable()
class CatchUpDataFactory extends PrizeDataFactory<CatchUpData> implements ICatchUpDataFactory {
  protected getGamePrizeClass(gamePrizeClassDescriptor: IPrizeClassDescriptor): new (node: IXmlNode) => CatchUpData {
    return gamePrizeClassDescriptor.catchUpDataClass;
  }

  protected getGamePrizeDefaultClass(): new (node: IXmlNode) => CatchUpData {
    return DefaultCatchUpData;
  }
}

@injectable()
class NextGameDataFactory extends PrizeDataFactory<NextGameData> implements INextGameDataFactory {
  protected getGamePrizeClass(gamePrizeClassDescriptor: IPrizeClassDescriptor): new (node: IXmlNode) => NextGameData {
    return gamePrizeClassDescriptor.nextGameDataClass;
  }

  protected getGamePrizeDefaultClass(): new (node: IXmlNode) => NextGameData {
    return DefaultNextGameData;
  }
}

@injectable()
class ResetDataFactory extends PrizeDataFactory<ResetData> implements IResetDataFactory {
  protected getGamePrizeClass(gamePrizeClassDescriptor: IPrizeClassDescriptor): new (node: IXmlNode) => ResetData {
    return gamePrizeClassDescriptor.resetDataClass;
  }

  protected getGamePrizeDefaultClass(): new (node: IXmlNode) => ResetData {
    return DefaultResetData;
  }
}

@injectable()
class BoxNumbersDataFactory extends PrizeDataFactory<BoxNumbersData> implements IBoxNumbersDataFactory {
  protected getGamePrizeClass(gamePrizeClassDescriptor: IPrizeClassDescriptor): new (node: IXmlNode) => BoxNumbersData {
    return gamePrizeClassDescriptor.boxNumbersDataClass;
  }

  protected getGamePrizeDefaultClass(): new (node: IXmlNode) => BoxNumbersData {
    return DefaultBoxNumbers;
  }
}

export {
  IPrizeDataFactory,
  ICallsDataFactory,
  ICatchUpDataFactory,
  INextGameDataFactory,
  IBoxNumbersDataFactory,
  IResetDataFactory,
  CallsDataFactory,
  CatchUpDataFactory,
  NextGameDataFactory,
  ResetDataFactory,
  BoxNumbersDataFactory
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/_domain/server/_factory/prize-data-factory.ts